/*
*######################################
* eWebEditor for Vue2 V1.6 - Advanced online web based WYSIWYG HTML editor.
* Copyright (c) 2003-2023 eWebSoft.com
*
* For further information go to http://www.ewebeditor.net/
* This copyright notice MUST stay intact for use.
*######################################
*/


/* global EWEBEDITOR */


let promise;
function getEditorNamespace( editorURL, onNamespaceLoaded ) {
	if ( 'EWEBEDITOR' in window ) {
		return Promise.resolve( EWEBEDITOR );
	}

	if ( typeof editorURL !== 'string' || editorURL.length < 1 ) {
		return Promise.reject( new TypeError( 'EWEBEDITOR URL must be a non-empty string.' ) );
	}

	if ( !promise ) {
		promise = getEditorNamespace.scriptLoader( editorURL ).then( res => {
			if ( onNamespaceLoaded ) {
				onNamespaceLoaded( res );
			}

			return res;
		} );
	}

	return promise;
}

getEditorNamespace.scriptLoader = editorURL => new Promise( ( scriptResolve, scriptReject ) => {
	loadScript( editorURL, err => {
		promise = undefined;

		if ( err ) {
			return scriptReject( err );
		} else if ( !window.EWEBEDITOR ) {
			return scriptReject( new Error( 'Script loaded from editorUrl doesn\'t provide EWEBEDITOR namespace.' ) );
		}

		scriptResolve( EWEBEDITOR );
	} );
} );


function loadScript( src, opts, cb ) {
	const head = document.head || document.getElementsByTagName( 'head' )[ 0 ];
	const script = document.createElement( 'script' );

	if ( typeof opts === 'function' ) {
		cb = opts;
		opts = {};
	}

	opts = opts || {};
	cb = cb || function() {};

	script.type = opts.type || 'text/javascript';
	script.charset = opts.charset || 'utf8';
	script.async = 'async' in opts ? !!opts.async : true;
	script.src = src;

	if ( opts.attrs ) {
		setAttributes( script, opts.attrs );
	}

	if ( opts.text ) {
		script.text = String( opts.text );
	}

	const onend = 'onload' in script ? stdOnEnd : ieOnEnd;
	onend( script, cb );

	if ( !script.onload ) {
		stdOnEnd( script, cb );
	}

	head.appendChild( script );
}

function setAttributes( script, attrs ) {
	for ( const attr in attrs ) {
		script.setAttribute( attr, attrs[ attr ] );
	}
}

function stdOnEnd( script, cb ) {
	script.onload = function() {
		this.onerror = this.onload = null;
		cb( null, script );
	};
	script.onerror = function() {
		this.onerror = this.onload = null;
		cb( new Error( 'Failed to load ' + this.src ), script );
	};
}

function ieOnEnd( script, cb ) {
	script.onreadystatechange = function() {
		if ( this.readyState != 'complete' && this.readyState != 'loaded' ) {
			return;
		}
		this.onreadystatechange = null;
		cb( null, script );
	};
}

function debounce( fn, delay, context = {} ) {
	let cancel;

	return function( ...args ) {
		clearTimeout( cancel );
		cancel = setTimeout( fn.bind( context, ...args ), delay );
	};
}




export default {
	name: 'EwebEditor',

	render( createElement ) {
		if(!this._id){
			this._Config = this._GetConfigFromProp();
			var s_Id = this._Config["id"];
			if(!s_Id){
				if(window.EWEBEDITOR_ID_INDEX){
					window.EWEBEDITOR_ID_INDEX = window.EWEBEDITOR_ID_INDEX + 1;
				}else{
					window.EWEBEDITOR_ID_INDEX = 1;
				}
				s_Id = "ewebeditor_auto_id_"+window.EWEBEDITOR_ID_INDEX;
			}
			this._id = s_Id;
		}

		return createElement( 'textarea', {
            attrs:{
                'spellcheck':'false',
                'autocorrect':'off',
				'id':this._id
            },
            style:{
                'display':'none'
            }
        });
	},

	props: {
		value: {
			type: String,
			default: ''
		},
		frontendurl: {
			type: String,
			default: '/ewebeditor/'
		},
		backendurl: {
			type: String,
			default: ''
		},
		config: {
			default: 'style:coolblue;width:550px;height:350px'
		},
		updatevaluetimer: {
			type: Number,
			default: 100
		}

	},

	data() {
		return {
			_id: '',
			_Config: {},
			_LastHtml: null
		};
	},

	mounted() {
		getEditorNamespace( this.frontendurl+'ewebeditor.js', namespace => {
			this.$emit( 'namespaceloaded', namespace );
		} ).then( () => {
			if ( this.$_destroyed ) {
				return;
			}
			
			var that = this;

			var f_OnChange = debounce( ev => {
				var editor = that.instance;
				const s_Html = editor.getHTML();
				that._LastHtml = s_Html;
				if ( that.value != s_Html ) {
					that.$emit( 'input', s_Html, ev);
				}
			}, that.updatevaluetimer );

			var f_NewConfigOn = function(ev){
				switch(ev.flag){
				case 'LoadComplete':
					that.$emit( 'ready', that.instance );
					break;

				case 'Change':
					if(that.updatevaluetimer>0){
						f_OnChange(ev);
					}
					break;
				}

				that.$emit( 'on', ev);
			};

			this._Config.on = f_NewConfigOn;
			this.$el.value = this.value;
			this.instance = EWEBEDITOR.Replace(this._id, this._Config);
		} );
	},

	beforeDestroy() {
		if ( this.instance && this.instance.Remove ) {
			this.instance.Remove(true);
		}

		this.$_destroyed = true;
	},

	watch: {
		value( s_NewValue ) {
			if ( this.instance && (this._LastHtml==null || s_NewValue!=this._LastHtml)) {
				EWEBEDITOR.SetHtmlAsync(this._id, s_NewValue);
			}
		}
	},

	methods: {
		_GetConfigFromProp() {
			var v_Config = this.config;
			var o_Config = {};
			if(typeof(v_Config)=='string'){
				var a = v_Config.split(';');
				for (var i=0 ; i<a.length ; i++) {
					var kv = a[i];
					var n = kv.indexOf(':');
					if (n>0){
						var s_Key = kv.substring(0, n).trim();
						if (s_Key){
							var s_Value = kv.substring(n+1).trim();
							s_Value = s_Value.replace(/(^['"]|['"]$)/gi, '');
							o_Config[s_Key] = s_Value;
						}
					}
				}
			}else if(typeof(v_Config)=='object'){
				o_Config = {...v_Config};
			}

			if(this.backendurl){
				o_Config.backendurl = this.backendurl;
			}

			return o_Config;
		}

	}
};